<template>
  <div
    ref="mapContainer"
    class="tianditu-map"
  />
</template>

<script>
import tiandituLabelLayout from './tiandituLabelLayout';
import tiandituSmokeBuildings from './tiandituSmokeBuildings';
import tiandituFireStations from './tiandituFireStations';
import tiandituBaihedongBoundary from './tiandituBaihedongBoundary';
import {
  bindTiandituTileRetry,
  destroyTiandituMap,
  loadTiandituMap
} from '@/utils/tiandituMapLoader';
import {
  convertPointListToTianditu,
  convertToTianditu,
  getTiandituCoordinateSystem
} from '@/utils/mapCoordinate';

// 全国总览使用地理经纬度，不随业务 BD09 来源配置重复转换。
const CHINA_OVERVIEW = { lng: 104.1954, lat: 35.8617, zoom: 4, coordinateSystem: 'WGS84' };
const DEFAULT_CENTER = CHINA_OVERVIEW;
// 临时调试开关：开启时所有身份默认定位到白鹤洞街道，关闭后仅特殊身份生效。
const DEBUG_FORCE_SPECIAL_LOCATION = true;
const SPECIAL_DEPT_CODE = '2c9d8091903a4e5b01904860cda24635';
// 白鹤洞固定点来自百度拾取，显式保留 BD09 来源。
const SPECIAL_CAPACITY_LOCATION = { lng: 113.250550, lat: 23.082530, zoom: 15, coordinateSystem: 'BD09' };
// 琶洲账号沿用原百度地图的固定定位规则和调整后的街道坐标。
const PAZHOU_CAPACITY_CODE = 'pazhou';
const PAZHOU_LOCATION = { lng: 113.375426, lat: 23.095921, zoom: 15, coordinateSystem: 'BD09' };
const TIANDITU_LABEL_DEFAULT_OFFSET = { x: 12, y: -15 };
// 名称底衬的内边距同时计入定位，保持标签居中及与图标的间距。
const LABEL_FONT_SIZE = 18;
const LABEL_PADDING_X = 8;
const LABEL_PADDING_Y = 4;
const SELECTED_LABEL_GAP = 6;
const HOVER_MARKER_LIFT = 8;

export default {
  name: 'TiandituMap',
  mixins: [tiandituLabelLayout, tiandituBaihedongBoundary, tiandituSmokeBuildings, tiandituFireStations],
  props: {
    dwList: {
      type: Array,
      default: () => []
    },
    capacityName: {
      type: String,
      default: ''
    },
    deptCode: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      map: null,
      mapReady: false,
      markerLayers: [],
      titleMarker: [],
      titleSourceMarker: null,
      hideTitleTimer: null,
      currentDw: null,
      currentJzw: null,
      currentPosition: null,
      resetMapTimer: null,
      hasManualViewport: false,
      hasOverviewPosition: false,
      unbindTileRetry: null
    };
  },
  methods: {
    isMapReady () {
      return this.mapReady && !!this.map && !!window.T;
    },
    isSamePosition (left, right) {
      if (!left || !right) return false;
      return Math.abs(Number(left.lng) - Number(right.lng)) < 0.000001 &&
        Math.abs(Number(left.lat) - Number(right.lat)) < 0.000001 &&
        Number(left.zoom || 0) === Number(right.zoom || 0);
    },
    getMapPoint (position) {
      if (!this.isMapReady()) return null;
      const converted = convertToTianditu(position, position.coordinateSystem);
      if (!converted) return null;
      return new window.T.LngLat(converted.lng, converted.lat);
    },
    getTiandituLabelOffset (offsetWidth = 0, offsetHeight = 0) {
      // 天地图 Label 默认会叠加 (12, -15) 的锚点偏移，这里转换为目标像素位置。
      return new window.T.Point(
        Number(offsetWidth) - TIANDITU_LABEL_DEFAULT_OFFSET.x,
        Number(offsetHeight) - TIANDITU_LABEL_DEFAULT_OFFSET.y
      );
    },
    getLabelOffsetWidth (content, fontSize) {
      const text = String(content || '');
      const size = Number(fontSize) || LABEL_FONT_SIZE;
      const fallback = -(size * text.length / 2 + LABEL_PADDING_X);
      const container = this.$refs.mapContainer;
      if (!container || !text) return fallback;

      const element = document.createElement('span');
      element.textContent = text;
      element.style.position = 'absolute';
      element.style.visibility = 'hidden';
      element.style.whiteSpace = 'nowrap';
      element.style.fontSize = `${size}px`;
      element.style.fontWeight = '500';
      element.style.lineHeight = `${size}px`;
      container.appendChild(element);
      const width = element.getBoundingClientRect().width;
      container.removeChild(element);
      return width > 0 ? -(width / 2 + LABEL_PADDING_X) : fallback;
    },
    emitChangeDetail (flag, dwId, jzwId) {
      // 进入详情只取消待执行的总览定位，保留随地理位置展示的区域边界。
      if (flag) {
        clearTimeout(this.resetMapTimer);
        this.resetMapTimer = null;
      }
      this.scheduleBaihedongUpdate();
      this.$emit('change-detail', flag, dwId, jzwId);
      this.$nextTick(this.scheduleAutoUnitLabels);
    },
    bindMapEvent (target, eventName, handler) {
      if (!target) return;
      if (target.addEventListener) target.addEventListener(eventName, handler);
      else if (target.on) target.on(eventName, handler);
    },
    changeDw (dw) {
      if (!this.isMapReady() || !dw) return;
      clearTimeout(this.resetMapTimer);
      this.resetMapTimer = null;
      this.currentDw = dw.code;
      const position = this.getDwPosition(dw);
      if (position && !this.isSamePosition(this.currentPosition, position)) {
        this.flyTo(position);
      }
      this.addMapMarkers(this.dwList, dw);
      this.scheduleBaihedongUpdate();
    },
    flyTo (position) {
      if (!this.isMapReady() || !position) return;
      const point = this.getMapPoint(position);
      if (!point) return;
      const zoom = Number(position.zoom || 19);
      if (typeof this.map.centerAndZoom === 'function') {
        this.map.centerAndZoom(point, zoom);
      } else {
        if (typeof this.map.panTo === 'function') this.map.panTo(point);
        if (typeof this.map.setZoom === 'function') this.map.setZoom(zoom);
      }
      this.currentPosition = position;
      this.hasManualViewport = false;
      this.scheduleBaihedongUpdate();
    },
    // 首帧和返回总览共用身份定位规则，避免刷新时先出现全国中心。
    getFixedOverviewLocation () {
      if (this.smokeBuildings !== null || DEBUG_FORCE_SPECIAL_LOCATION || this.deptCode === SPECIAL_DEPT_CODE) {
        return SPECIAL_CAPACITY_LOCATION;
      }
      if (this.capacityName === PAZHOU_CAPACITY_CODE) return PAZHOU_LOCATION;
      return null;
    },
    isBaihedongOverview () {
      return this.getFixedOverviewLocation() === SPECIAL_CAPACITY_LOCATION;
    },
    // 地图操作只阻止数据刷新拉回视野，不销毁区域展示层。
    handleViewportStart () {
      this.pauseAutoUnitLabels();
      this.hasManualViewport = true;
      this.currentPosition = null;
      clearTimeout(this.resetMapTimer);
      this.resetMapTimer = null;
      this.scheduleBaihedongUpdate();
    },
    getDwPosition (dw) {
      if (!dw) return null;
      let position = { lng: dw.lng, lat: dw.lat, zoom: 19 };
      if (dw.jzqbz === 'Y' && Array.isArray(dw.jzwList) && dw.jzwList.length > 0) {
        position = this.getCenterPoint(dw.jzwList.map(jzw => ({
          lng: jzw.dzLng,
          lat: jzw.dzLat
        })));
      }
      return position;
    },
    resetMap () {
      if (!this.isMapReady()) return;
      const fixedLocation = this.getFixedOverviewLocation();
      // 普通身份的总览可能覆盖全市或全国，自动名称仍从街道级视野开始。
      this.labelOverviewZoom = fixedLocation ? fixedLocation.zoom : SPECIAL_CAPACITY_LOCATION.zoom;
      // 广钢金业展厅身份默认展示白鹤洞街道，避免按该身份下单位点位计算总览范围。
      if (fixedLocation) {
        if (fixedLocation === SPECIAL_CAPACITY_LOCATION) this.showBaihedongOverview(CHINA_OVERVIEW.zoom);
        else this.flyTo(fixedLocation);
      } else if (this.dwList.length > 0) {
        this.fitToPoints(this.dwList);
      } else {
        this.flyTo(DEFAULT_CENTER);
      }
      // 固定场景或有效单位点已完成总览定位；空数据时等待首批有效点补定位。
      // 不用 currentPosition 判断首次定位，因为总览会主动清空该交互位置。
      this.hasOverviewPosition = !!fixedLocation || convertPointListToTianditu(this.dwList).length > 0;
      this.currentPosition = null;
      this.hasManualViewport = false;
      this.scheduleBaihedongUpdate();
      this.finishAutoUnitLabels();
      this.updateSmokeVisibility();
      this.syncFireStations();
    },
    scheduleResetMap () {
      if (!this.isMapReady()) return;
      clearTimeout(this.resetMapTimer);
      this.resetMapTimer = setTimeout(() => {
        this.resetMapTimer = null;
        if (this.isMapReady()) this.resetMap();
      }, 0);
    },
    fitToPoints (pointList) {
      if (!this.isMapReady()) return;
      const convertedPoints = convertPointListToTianditu(pointList)
        .map(point => new window.T.LngLat(point.lng, point.lat));
      if (convertedPoints.length <= 1) {
        const firstPoint = pointList.find(point => convertToTianditu(point));
        this.flyTo(firstPoint || DEFAULT_CENTER);
        return;
      }
      if (typeof this.map.getViewport === 'function' && typeof this.map.centerAndZoom === 'function') {
        const viewport = this.map.getViewport(convertedPoints);
        if (viewport && viewport.center && Number.isFinite(Number(viewport.zoom))) {
          const center = new window.T.LngLat(viewport.center.lng, viewport.center.lat);
          this.map.centerAndZoom(center, Math.max(CHINA_OVERVIEW.zoom, Number(viewport.zoom) - 1));
          return;
        }
      }
      if (typeof this.map.setViewport === 'function') this.map.setViewport(convertedPoints);
    },
    addMapMarkerEvent () {
      if (!this.isMapReady()) return;
      this.markerLayers
        .filter(marker => marker.hm_marker && !marker.hm_eventsBound && !this.titleMarker.includes(marker))
        .forEach(marker => {
          // 复用标记只绑定一次事件，触发时读取最新业务数据，避免闭包保留旧建筑信息。
          marker.hm_eventsBound = true;
          this.bindMapEvent(marker, 'click', () => {
            const dw = marker.hm_data;
            if (!dw) return;
            const isBack = this.currentDw === dw.code;
            if (dw.markerType === 'jzw' && dw.jzwId !== this.currentJzw) {
              this.currentJzw = dw.jzwId;
              this.emitChangeDetail(true, dw.code, dw.jzwId);
              return;
            }
            if (isBack) {
              this.resetMap();
              this.emitChangeDetail(false);
              this.currentDw = null;
            } else {
              this.flyTo(this.getDwPosition(dw));
              this.emitChangeDetail(true, dw.code);
              this.currentDw = dw.code;
            }
            this.addMapMarkers(this.dwList, isBack ? null : dw);
          });

          if (marker.hm_mouseover !== 'none') {
            this.bindMapEvent(marker, 'mouseover', () => {
              this.cancelHideTitleMarker();
              // 从标题框返回原单位时复用标签，避免重建造成闪烁。
              if (this.titleSourceMarker === marker) return;
              this.cleanTitleMarker();
              this.addTitleMarker(marker.hm_data, marker);
            });
            this.bindMapEvent(marker, 'mouseout', () => {
              this.scheduleHideTitleMarker(marker);
            });
          }
        });
    },
    addTitleMarker (dw, sourceMarker) {
      if (!dw) return;
      const icon = {
        width: 134,
        height: 59,
        url: '/monitor/images/bdmap/marker_title.png',
        anchorY: 59 + HOVER_MARKER_LIFT
      };
      const name = String(dw.name || '');
      const content = name.substr(0, 6);
      const sign = name.length > 6 ? '..' : '';
      const fontSize = LABEL_FONT_SIZE;
      const label = {
        content: content + sign,
        fontSize,
        color: '#FFF',
        offsetWidth: this.getLabelOffsetWidth(content + sign, fontSize),
        offsetHeight: -icon.height + 8 - HOVER_MARKER_LIFT - LABEL_PADDING_Y * 2
      };
      const titleMarker = this.addMapMarker(dw, icon, label);
      if (!titleMarker) return;
      this.titleMarker.push(titleMarker);
      this.titleSourceMarker = sourceMarker;
      this.suppressAutoUnitLabel(dw.code);
      this.bindMapEvent(titleMarker, 'click', () => {
        // 悬浮期间可能收到单位刷新，点击仍使用来源标记的最新坐标和建筑列表。
        const dw = sourceMarker.hm_data;
        this.flyTo(this.getDwPosition(dw));
        this.emitChangeDetail(true, dw.code);
        this.currentDw = dw.code;
        this.addMapMarkers(this.dwList, dw);
      });
      this.bindMapEvent(titleMarker, 'mouseover', () => {
        if (this.titleSourceMarker === sourceMarker) this.cancelHideTitleMarker();
      });
      this.bindMapEvent(titleMarker, 'mouseout', () => {
        this.scheduleHideTitleMarker(sourceMarker);
      });
    },
    cancelHideTitleMarker () {
      clearTimeout(this.hideTitleTimer);
      this.hideTitleTimer = null;
    },
    // 单位图标和可点击标题框共享悬浮状态，短暂延迟允许鼠标跨越两者间隙。
    scheduleHideTitleMarker (sourceMarker) {
      if (this.titleSourceMarker !== sourceMarker) return;
      this.cancelHideTitleMarker();
      this.hideTitleTimer = setTimeout(() => this.cleanTitleMarker(), 150);
    },
    cleanTitleMarker () {
      this.cancelHideTitleMarker();
      // 先清除所属单位，防止移除旧覆盖物产生的事件影响新单位标签。
      this.titleSourceMarker = null;
      if (!this.map) return;
      const titleLayers = [];
      this.titleMarker.forEach(marker => {
        titleLayers.push(marker);
        if (marker.hm_label) titleLayers.push(marker.hm_label);
      });
      titleLayers.forEach(layer => this.map.removeLayer && this.map.removeLayer(layer));
      this.markerLayers = this.markerLayers.filter(layer => !titleLayers.includes(layer));
      this.titleMarker = [];
      this.scheduleAutoUnitLabels();
    },
    // 数据轮询复用显示未变化的覆盖物；主动切换单位仍按原流程重建选中态。
    addMapMarkers (dwList = [], selectDw, reuseExisting = false) {
      if (!this.isMapReady()) return;
      // 烟感模式绘制建筑聚合点，不使用单位标记与单位详情事件。
      if (this.smokeBuildings !== null) {
        this.drawSmokeBuildings();
        return;
      }
      const reusableMarkers = new Map();
      const previousLayers = reuseExisting ? this.markerLayers.filter(layer => !this.smokeLayers.includes(layer))
        .filter(layer => !this.titleMarker.some(marker => marker === layer || marker.hm_label === layer)) : [];
      if (reuseExisting) {
        // 悬浮标题和烟感入口独立保留，单位按业务主键匹配，不能按返回顺序匹配。
        previousLayers.filter(marker => marker.hm_marker)
          .forEach(marker => reusableMarkers.set(marker.hm_key, marker));
        this.markerLayers = this.markerLayers.filter(layer => this.smokeLayers.includes(layer) ||
          this.titleMarker.some(marker => marker === layer || marker.hm_label === layer));
      } else this.cleanAllMarker();
      if (selectDw) this.suppressAutoUnitLabel(selectDw.code);
      let otherDwList = dwList.slice();
      if (selectDw) {
        otherDwList = otherDwList.filter(el => el.code !== selectDw.code);
        if (selectDw.jzqbz === 'Y' && Array.isArray(selectDw.jzwList)) {
          selectDw.jzwList.forEach((jzw, index) => {
            if (jzw.dzLng === null || jzw.dzLng === undefined || jzw.dzLat === null || jzw.dzLat === undefined) return;
            const icon = {
              width: 91,
              height: 115,
              url: '/monitor/images/bdmap/marker_online_selected.png'
            };
            const name = String(jzw.jzwmc || '');
            const content = name.substr(0, 7);
            const label = {
              content: content + (name.length > 7 ? '..' : ''),
              fontSize: LABEL_FONT_SIZE,
              color: '#FFF',
              offsetWidth: this.getLabelOffsetWidth(content + (name.length > 7 ? '..' : ''), LABEL_FONT_SIZE),
              offsetHeight: -icon.height - LABEL_FONT_SIZE - LABEL_PADDING_Y * 2 - SELECTED_LABEL_GAP
            };
            const markerData = {
              lat: jzw.dzLat,
              lng: jzw.dzLng,
              code: selectDw.code,
              jzwId: jzw.jzwId,
              markerType: 'jzw'
            };
            // 轮询保留当前建筑选择；该建筑被移除时才沿用首栋建筑的默认选择。
            if (index === 0 && (!reuseExisting || !selectDw.jzwList.some(item => item.jzwId === this.currentJzw))) {
              this.currentJzw = jzw.jzwId;
            }
            const marker = this.addMapMarker(markerData, icon, label, reusableMarkers);
            if (marker) marker.hm_mouseover = 'none';
          });
        } else {
          const icon = {
            width: 91,
            height: 115,
            url: '/monitor/images/bdmap/marker_online_selected.png'
          };
          const name = String(selectDw.name || '');
          const content = name.substr(0, 6);
          const label = {
            content: content + (name.length > 6 ? '..' : ''),
            fontSize: LABEL_FONT_SIZE,
            color: '#FFF',
            offsetWidth: this.getLabelOffsetWidth(content + (name.length > 6 ? '..' : ''), LABEL_FONT_SIZE),
            offsetHeight: -icon.height - LABEL_FONT_SIZE - LABEL_PADDING_Y * 2 - SELECTED_LABEL_GAP
          };
          const marker = this.addMapMarker(selectDw, icon, label, reusableMarkers);
          if (marker) marker.hm_mouseover = 'none';
        }
      }

      otherDwList.forEach(dw => {
        const icon = {
          width: dw.lwzt === '1' ? 32 : 25.6,
          height: dw.lwzt === '1' ? 40 : 32,
          url: dw.lwzt === '1' ? '/monitor/images/bdmap/marker_online.png' : '/monitor/images/bdmap/marker_unline.png'
        };
        this.addMapMarker(dw, icon, false, reusableMarkers);
      });
      if (reuseExisting) {
        // 来源被删除或显示发生变化时关闭旧悬浮框，未变化的悬浮态保持连续。
        if (this.titleSourceMarker && !this.markerLayers.includes(this.titleSourceMarker)) this.cleanTitleMarker();
        previousLayers.filter(layer => !this.markerLayers.includes(layer)).forEach(layer => this.map.removeLayer(layer));
      }
      this.addMapMarkerEvent();
      // 单位轮询不重建独立烟感入口，身份和选中态切换仍由完整更新处理。
      if (!reuseExisting) this.drawSmokeBuildings();
      this.scheduleAutoUnitLabels();
    },
    addMapMarker (dw, icon, addLabel = false, reusableMarkers = null) {
      if (!this.isMapReady() || !dw || !icon) return null;
      const point = this.getMapPoint(dw);
      if (!point) return null;
      const key = JSON.stringify([dw.code, dw.markerType || 'dw', dw.jzwId]);
      // 比较地图实际使用的展示字段；其他业务字段只更新引用，不触发图层重建。
      const signature = JSON.stringify([point.lng, point.lat, dw.name, icon, addLabel]);
      const existing = reusableMarkers && reusableMarkers.get(key);
      if (existing && existing.hm_signature === signature) {
        existing.hm_data = dw;
        this.markerLayers.push(existing);
        if (existing.hm_label) this.markerLayers.push(existing.hm_label);
        reusableMarkers.delete(key);
        return existing;
      }
      const api = window.T;
      const anchorY = icon.anchorY || icon.height;
      const mapIcon = new api.Icon({
        iconUrl: icon.url,
        iconSize: [icon.width, icon.height],
        iconAnchor: [icon.width / 2, anchorY]
      });
      const marker = new api.Marker(point, { icon: mapIcon });
      marker.hm_data = dw;
      marker.hm_key = key;
      marker.hm_signature = signature;
      marker.hm_marker = true;
      this.map.addLayer(marker);
      this.markerLayers.push(marker);

      if (addLabel) {
        const label = new api.Label({
          text: addLabel.content,
          position: point,
          offset: this.getTiandituLabelOffset(addLabel.offsetWidth || 0, addLabel.offsetHeight || 0),
          className: 'tianditu-map-label'
        });
        this.map.addLayer(label);
        const element = label.getElement && label.getElement();
        if (element) {
          element.style.color = addLabel.color;
          element.style.fontSize = `${addLabel.fontSize}px`;
          element.style.lineHeight = `${addLabel.fontSize}px`;
          element.style.border = 'none';
          element.style.padding = `${LABEL_PADDING_Y}px ${LABEL_PADDING_X}px`;
          element.style.pointerEvents = 'none';
        }
        marker.hm_label = label;
        this.markerLayers.push(label);
      }
      return marker;
    },
    cleanAllMarker () {
      if (!this.map) return;
      this.cleanTitleMarker();
      // 仅清理单位及名称，保留独立的街道展示覆盖物。
      this.markerLayers.forEach(layer => this.map.removeLayer(layer));
      this.markerLayers = [];
      this.titleMarker = [];
    },
    getCenterPoint (pointList = []) {
      const convertedPoints = convertPointListToTianditu(pointList);
      if (convertedPoints.length === 0) return { ...DEFAULT_CENTER };

      // 建筑群沿用原地图的自适应视野语义，确保分散的建筑物都能进入可视区域。
      if (typeof this.map.getViewport === 'function') {
        const points = convertedPoints.map(point => new window.T.LngLat(point.lng, point.lat));
        const viewport = this.map.getViewport(points);
        if (viewport && viewport.center && Number.isFinite(Number(viewport.zoom))) {
          return {
            lng: Number(viewport.center.lng),
            lat: Number(viewport.center.lat),
            zoom: Number(viewport.zoom) - 1,
            coordinateSystem: getTiandituCoordinateSystem()
          };
        }
      }

      const longitudes = convertedPoints.map(point => point.lng);
      const latitudes = convertedPoints.map(point => point.lat);
      return {
        lng: (Math.min(...longitudes) + Math.max(...longitudes)) / 2,
        lat: (Math.min(...latitudes) + Math.max(...latitudes)) / 2,
        zoom: convertedPoints.length > 1 ? 18 : 19,
        coordinateSystem: getTiandituCoordinateSystem()
      };
    }
  },
  watch: {
    capacityName () {
      if (!this.isMapReady()) return;
      this.clearAutoUnitLabels();
      // 身份切换必须退出旧详情并重新选择总览，不能沿用上一场景的标签。
      this.currentDw = null;
      this.currentJzw = null;
      this.emitChangeDetail(false);
      this.resetMap();
      this.addMapMarkers(this.dwList);
    },
    dwList: {
      deep: true,
      handler (newVal, oldVal) {
        if (!this.isMapReady() || newVal === oldVal) return;
        // 仅补齐首次有效数据的总览定位；后续轮询不得反复重置底图镜头。
        if (!this.hasOverviewPosition && !this.hasManualViewport && this.currentDw === null) this.scheduleResetMap();
        const selectedDw = this.currentDw === null ? null : newVal.find(dw => dw.code === this.currentDw);
        this.addMapMarkers(newVal || [], selectedDw, true);
      }
    }
  },
  async mounted () {
    try {
      await loadTiandituMap();
      if (this._isBeingDestroyed) return;
      const api = window.T;
      const mapOptions = {
        // 保留总览最低缩放，避免滚轮缩放退到世界级别。
        minZoom: CHINA_OVERVIEW.zoom
      };
      const map = this.map = new api.Map(this.$refs.mapContainer, mapOptions);
      this.mapReady = true;
      const initialLocation = this.getFixedOverviewLocation() || DEFAULT_CENTER;
      map.centerAndZoom(this.getMapPoint(initialLocation), initialLocation.zoom);
      map.enableScrollWheelZoom(true);
      if (typeof map.enableDrag === 'function') map.enableDrag();
      map.setStyle('indigo');
      this.bindMapEvent(map, 'movestart', this.handleViewportStart);
      this.bindMapEvent(map, 'zoomstart', this.handleViewportStart);
      this.bindBaihedongBoundaryEvents();
      this.bindSmokeBuildingEvents();
      this.bindFireStationEvents();
      ['moveend', 'zoomend', 'resize'].forEach(eventName => {
        this.bindMapEvent(map, eventName, this.finishAutoUnitLabels);
      });
      this.unbindTileRetry = bindTiandituTileRetry(this.$refs.mapContainer, detail => {
        // 单瓦片重试耗尽时只上报，不重建地图，避免网络故障期间覆盖物和视野反复闪烁。
        this.$emit('map-error', new Error(`天地图瓦片重试失败: ${detail.src}`));
      });
      this.addMapMarkers(this.dwList);
      this.scheduleResetMap();
      this.$emit('map-ready');
    } catch (error) {
      this.$emit('map-error', error);
    }
  },
  beforeDestroy () {
    this.cleanTitleMarker();
    this.clearAutoUnitLabels();
    clearTimeout(this.resetMapTimer);
    this.resetMapTimer = null;
    if (this.unbindTileRetry) this.unbindTileRetry();
    this.unbindTileRetry = null;
    destroyTiandituMap(this.map);
    this.map = null;
    this.mapReady = false;
    this.markerLayers = [];
    this.titleMarker = [];
  }
};
</script>

<style lang="scss">
.tianditu-map {
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: #131124;
}

// indigo 将注记瓦片降至半透明；仅提亮已有中文注记，保留道路底图的暗色层次。
// 同时覆盖墨卡托与经纬度瓦片地址，缩放或拖动加载的新注记自动沿用样式。
.tianditu-map .tdtTileImage[src*="/cva_w/wmts"],
.tianditu-map .tdtTileImage[src*="/cva_c/wmts"] {
  opacity: 0.82 !important;
  filter: grayscale(1) invert(1) brightness(1.3) contrast(0.9);
}

// 自定义 className 会替换 SDK 默认标签类，须补齐绝对定位，避免标签相互挤占文档流。
.tianditu-map .tianditu-map-label {
  position: absolute;
}

// 仅增强业务名称的对比度，不改变底图瓦片及其原生地名。
.tianditu-map-label {
  background: rgba(3, 18, 40, 0.9);
  border-radius: 4px;
  font-weight: 500;
  box-shadow: 0 0 0 1px rgba(57, 196, 255, 0.45);
  white-space: nowrap;
  user-select: none;
}

// 自动名称采用紧凑底衬，显式绝对定位；不拦截图标悬浮和地图拖动。
.tianditu-map .tianditu-auto-unit-label {
  position: absolute;
  box-sizing: border-box;
  padding: 3px 7px;
  border: 1px solid rgba(72, 168, 198, 0.35);
  border-radius: 3px;
  background: rgba(4, 22, 43, 0.84);
  color: #e5f3fa;
  font-size: 14px;
  line-height: 20px;
  white-space: nowrap;
  pointer-events: none;
  user-select: none;
}

// 天地图 Marker Pane 默认层级高于 Label Pane，避免标题图片覆盖白色单位名称。
.tianditu-map .tdt-overlay-pane {
  z-index: 700 !important;
}
</style>
